import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { getUser } from '../api/api';

const PermissionsContext = createContext();

export const PermissionsProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [permissions, setPermissions] = useState([]);
    const [role, setRole] = useState(null);
    const [loading, setLoading] = useState(true);

    const fetchUserPermissions = useCallback(async () => {
        try {
            setLoading(true);
            const userData = await getUser();

            const resolvedRole = userData.rôle || userData['rÃ´le'] || userData.role;
            setUser(userData);
            setRole(resolvedRole);

            let rawPerm = userData.permissions || userData.perm;
            let userPermissions = [];

            if (Array.isArray(rawPerm)) {
                userPermissions = rawPerm;
            } else if (typeof rawPerm === 'string') {
                try {
                    const parsed = JSON.parse(rawPerm);
                    if (Array.isArray(parsed)) {
                        userPermissions = parsed;
                    } else {
                        userPermissions = rawPerm.split(',').map(p => p.trim()).filter(Boolean);
                    }
                } catch {
                    userPermissions = rawPerm.split(',').map(p => p.trim()).filter(Boolean);
                }
            } else if (rawPerm && typeof rawPerm === 'object') {
                userPermissions = Object.values(rawPerm).map(p => String(p).trim());
            }

            setPermissions(userPermissions);
        } catch (error) {
            console.error('Erreur lors de la récupération des permissions:', error);
            setPermissions([]);
            setRole(null);
            setUser(null);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchUserPermissions();
    }, [fetchUserPermissions]);

    const hasPermission = useCallback((permission) => {
        if (!permissions || permissions.length === 0) return false;
        const normalizedPerms = permissions.map(p => String(p).toLowerCase());
        if (normalizedPerms.includes('allaccess')) return true;
        return normalizedPerms.includes(String(permission).toLowerCase());
    }, [permissions]);

    const hasRole = useCallback((requiredRole) => {
        if (!role) return false;
        const normalizedPerms = permissions.map(p => String(p).toLowerCase());
        if (normalizedPerms.includes('allaccess')) return true;
        const normalizedRole = role.toLowerCase().replace(/[Ã©]/g, 'é');
        const normalizedRequiredRole = String(requiredRole).toLowerCase();
        return normalizedRole === normalizedRequiredRole;
    }, [role, permissions]);

    const isAdminOrRegulator = useCallback(() => {
        return hasRole('admin') || hasRole('régulateur');
    }, [hasRole]);

    const value = {
        user,
        permissions,
        role,
        loading,
        hasPermission,
        hasRole,
        isAdminOrRegulator,
        refreshPermissions: fetchUserPermissions
    };

    return (
        <PermissionsContext.Provider value={value}>
            {children}
        </PermissionsContext.Provider>
    );
};

export const usePermissionsContext = () => {
    const context = useContext(PermissionsContext);
    if (context === undefined) {
        throw new Error('usePermissionsContext must be used within a PermissionsProvider');
    }
    return context;
};
